import { forwardRef } from 'react';
import { Card, CardContent, Typography, Chip, Badge, Box, IconButton, Tooltip, Avatar, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import CommentIcon from '@mui/icons-material/Comment';
import ShareIcon from '@mui/icons-material/Share';
import { CommonFunction } from 'src/utils/commonFunctions/common-function.component';
import InsertLinkIcon from '@mui/icons-material/InsertLink';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';

// Styled Card component with modern social media styling and fixed size
const PostCardStyled = styled(Card)(({ theme, isSelected }) => ({
  marginBottom: theme.spacing(2),
  maxWidth: 500,
  minWidth:350,
  borderRadius: 16,
  border: isSelected ? `2px solid ${theme.palette.primary.main}` : 'none',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  cursor: 'pointer',
  backgroundColor: theme.palette.background.paper,
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
  },
  display: 'flex', // Ensure content stretches to fill the card
  flexDirection: 'column',
}));

// Styled media image
const PostImage = styled('img')({
  width: '100%',
 width: 450,
  height: 250,
  objectFit: 'cover',
  borderRadius: 10,
  display: 'block',
});

const PostCard = forwardRef(({ post, isSelected, selectedProfile, onSelect, isMobile }, ref) => {
  const isVideo = post.media_type === 'VIDEO' || post.link?.includes('videos');
  const imageUrl = post.image_url || post.media_url;
  const postText = post.caption || post.message || '';
  const postDate = post.timestamp || post.created_time;
  const username = selectedProfile.profile_name || 'Unknown User';
  const avatarUrl = selectedProfile.profile_image || null;

  const renderMedia = () => {
    if (!imageUrl) {
      return (
        <Box
          sx={{
            height: 250, // Match PostImage height
            borderRadius: 10,
            bgcolor: 'grey.100',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            mt: 1.5,
          }}
        >
          <InsertPhotoIcon sx={{ fontSize: 36, color: 'text.secondary' }} />
          <Chip
            label="No Media"
            size="small"
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              bgcolor: 'grey.300',
              color: 'text.primary',
              fontWeight: 500,
            }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ position: 'relative', mt: 1.5 }}>
        <PostImage src={imageUrl} alt={isVideo ? 'Video' : 'Image'} />
        <Chip
          label={isVideo ? 'Video' : 'Image'}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            bgcolor: isVideo ? 'primary.main' : 'secondary.main',
            color: 'white',
            fontWeight: 500,
            px: 1,
          }}
        />
      </Box>
    );
  };

  return (
    <Stack container justifyContent="center" alignItems="center">
      <PostCardStyled ref={ref} isSelected={isSelected} onClick={onSelect}>
        <CardContent
          sx={{
            p: isMobile ? 1.5 : 2.5,
            flexGrow: 1, // Ensure content stretches to fill card
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between', // Distribute content evenly
          }}
        >
          {/* User Info Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={CommonFunction.SocialMediaAccountImage(selectedProfile.service_name)}
              sx={{ mr: 2, zIndex: 0 }}
            >
              <Avatar
                src={avatarUrl}
                alt={username[0]?.toUpperCase()}
                sx={{ height: 40, width: 40, bgcolor: 'primary.main' }}
              />
            </Badge>
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="text.primary">
                {username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {dayjs(postDate).format('MMM D, YYYY · h:mm A')}
              </Typography>
            </Box>
          </Box>

          {/* Post Text */}
          {postText && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                fontSize: isMobile ? '0.9rem' : '0.95rem',
                color: 'text.primary',
                lineHeight: 1.5,
                display: '-webkit-box',
                WebkitLineClamp: 4,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                flexGrow: 1, // Allow text to take available space
              }}
            >
              {postText}
            </Typography>
          )}

          {/* Media Content */}
          {renderMedia()}

          {/* Interaction Bar */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: 1.5,
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Tooltip title="Likes">
              <Chip
                icon={<ThumbUpIcon fontSize="small" />}
                label={post.like_count || 0}
                size="small"
                sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
              />
            </Tooltip>
            <Tooltip title="Comments">
              <Chip
                icon={<CommentIcon fontSize="small" />}
                label={post.comment_count || 0}
                size="small"
                sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
              />
            </Tooltip>
            {post.share_count > 0 && (
              <Tooltip title="Shares">
                <Chip
                  icon={<ShareIcon fontSize="small" />}
                  label={post.share_count}
                  size="small"
                  sx={{ bgcolor: 'grey.100', '&:hover': { bgcolor: 'grey.200' } }}
                />
              </Tooltip>
            )}
            {post.link && (
              <Tooltip title="Open link">
                <IconButton
                  size="small"
                  href={post.link}
                  target="_blank"
                  sx={{ ml: 'auto', color: 'primary.main', '&:hover': { color: 'primary.dark' } }}
                >
                  <InsertLinkIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </PostCardStyled>
    </Stack>
  );
});

export default PostCard;